export interface SoloRunXpInput {
  bestLevelReached: number;
  totalScore: number;
}

export interface SoloRunEligibilityInput {
  bestLevelReached: number;
  durationSec: number;
}

export interface VersusRunXpInput {
  localFinalLevel: number;
  localScore: number;
  won: boolean;
  localElo: number;
  opponentElo: number;
  isBotMatch: boolean;
}

export type UnlockableFeature =
  | "solo"
  | "quickmatch"
  | "leaderboard"
  | "profile"
  | "dailyClaim"
  | "friends"
  | "addFriend"
  | "shop"
  | "gemShop"
  | "tokenShop"
  | "lobby"
  | "customLobbies"
  | "advancedCompetitive";

export const FIRST_WIN_DAILY_XP_BONUS = 20;

const FEATURE_UNLOCK_LEVELS: Record<UnlockableFeature, number> = {
  solo: 1,
  quickmatch: 1,
  leaderboard: 1,
  profile: 1,
  dailyClaim: 1,
  friends: 3,
  addFriend: 3,
  lobby: 3,
  shop: 8,
  gemShop: 8,
  tokenShop: 12,
  customLobbies: 10,
  advancedCompetitive: 20,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function wholeLevel(level: number): number {
  return Math.max(1, Math.floor(level));
}

/** XP required to advance from `level` to `level + 1`. */
export function xpForLevel(level: number): number {
  const currentLevel = wholeLevel(level);
  const n = currentLevel - 1;
  return Math.round(110 + (6 * n) + (0.02 * n * n));
}

/** Cumulative XP required to reach `level` from level 1. */
export function totalXpForLevel(level: number): number {
  const targetLevel = wholeLevel(level);
  let total = 0;
  for (let currentLevel = 1; currentLevel < targetLevel; currentLevel += 1) {
    total += xpForLevel(currentLevel);
  }
  return total;
}

/** Derive level + progress from cumulative total XP. */
export function levelFromTotalXp(totalXp: number): {
  level: number;
  xpIntoLevel: number;
  xpNeeded: number;
} {
  let remaining = Math.max(0, Math.floor(totalXp));
  let level = 1;
  while (remaining >= xpForLevel(level)) {
    remaining -= xpForLevel(level);
    level += 1;
  }
  return {
    level,
    xpIntoLevel: remaining,
    xpNeeded: xpForLevel(level),
  };
}

/** Add XP and return new state. Pure function — no side effects. */
export function addXp(
  currentTotalXp: number,
  amount: number,
): {
  totalXp: number;
  level: number;
  xpIntoLevel: number;
  xpNeeded: number;
  levelsGained: number;
} {
  const previousLevel = levelFromTotalXp(currentTotalXp).level;
  const totalXp = Math.max(0, Math.floor(currentTotalXp)) + Math.max(0, Math.floor(amount));
  const result = levelFromTotalXp(totalXp);
  return {
    totalXp,
    level: result.level,
    xpIntoLevel: result.xpIntoLevel,
    xpNeeded: result.xpNeeded,
    levelsGained: result.level - previousLevel,
  };
}

export function soloRunXp(input: SoloRunXpInput): number {
  const bestLevelReached = wholeLevel(input.bestLevelReached);
  const totalScore = Math.max(0, Math.floor(input.totalScore));
  return (8 + (2 * bestLevelReached))
    + Math.floor(totalScore / 12_000)
    + Math.max(0, bestLevelReached - 10);
}

export function isSoloRunXpEligible(input: SoloRunEligibilityInput): boolean {
  const bestLevelReached = wholeLevel(input.bestLevelReached);
  const durationSec = Math.max(0, Math.floor(input.durationSec));
  return bestLevelReached >= 4 || durationSec >= 90;
}

export function versusRunXp(input: VersusRunXpInput): number {
  const localFinalLevel = wholeLevel(input.localFinalLevel);
  const localScore = Math.max(0, Math.floor(input.localScore));
  const localElo = Math.max(0, Math.floor(input.localElo));
  const opponentElo = Math.max(0, Math.floor(input.opponentElo));
  const upsetBonus = input.won
    ? clamp(Math.floor((opponentElo - localElo) / 75), 0, 8)
    : 0;
  const total = (8 + (2 * localFinalLevel))
    + Math.floor(localScore / 15_000)
    + (input.won ? 10 : 0)
    + upsetBonus;
  if (input.isBotMatch) {
    return Math.floor(total * 0.85);
  }
  return total;
}

export function prestigeStarsForLevel(level: number): number {
  return clamp(Math.floor((wholeLevel(level) - 1) / 100), 0, 5);
}

export function featureUnlockLevel(feature: UnlockableFeature): number {
  return FEATURE_UNLOCK_LEVELS[feature];
}

export function isFeatureUnlocked(level: number, feature: UnlockableFeature): boolean {
  return wholeLevel(level) >= featureUnlockLevel(feature);
}
